// src/pages/Order/components/OrderSelection.jsx
// Order Selection Modal for linking Karigar Jobs to Orders
// Follows the same pattern as UserSelection component

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllOrdersForAdminThunk,
  clearOrderError,
} from "../../../store/thunks/orderThunks";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Modal from "../../../components/common/Modal";
import Badge from "../../../components/common/Badge";
import {
  SearchIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ShoppingBagIcon,
  UserIcon,
  PhoneIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  ClipboardListIcon,
  RefreshIcon,
  XCircleIcon,
  DocumentTextIcon,
  CubeIcon,
  ScaleIcon,
  SparklesIcon,
} from "@heroicons/react/outline";
import { format } from "date-fns";
import {
  notifyError,
  notifyInfo,
  notifySuccess,
} from "../../../utils/notification";

// Order Status Configuration
const ORDER_STATUSES = [
  { value: "", label: "All Statuses" },
  { value: "PENDING", label: "Pending", color: "warning" },
  { value: "CONFIRMED", label: "Confirmed", color: "info" },
  { value: "PROCESSING", label: "Processing", color: "primary" },
  { value: "READY", label: "Ready", color: "success" },
  { value: "COMPLETED", label: "Completed", color: "success" },
  { value: "CANCELLED", label: "Cancelled", color: "danger" },
];

// Work types that typically need karigar jobs
const KARIGAR_WORK_TYPES = [
  "NEW_MAKING",
  "REPAIR",
  "ALTERATION",
  "POLISHING",
  "RHODIUM",
  "RESIZING",
  "STONE_SETTING",
  "CUSTOM_ORDER",
  "ENGRAVING",
  "HALLMARKING",
];

const OrderSelection = ({
  isOpen = false,
  onClose,
  onOrderSelected,
  selectedOrder = null,
  title = "Select Order",
  showItemSelection = true,
  filterByKarigarWork = false,
  defaultStatusFilter = "",
}) => {
  const dispatch = useDispatch();
  const { orders, loadingList, error, pagination } = useSelector(
    (state) => state.orders
  );

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [localSelectedOrder, setLocalSelectedOrder] = useState(selectedOrder);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    filters: false,
  });
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch orders when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchOrders();
      // Focus search input
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [isOpen]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedOrder(selectedOrder);
      setSelectedItemIndex(null);
      setExpandedOrderId(null);
    }
  }, [isOpen, selectedOrder]);

  // Clear errors
  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearOrderError());
    }
  }, [error, dispatch]);

  const fetchOrders = useCallback(
    (search = "") => {
      const filters = {
        search: search || undefined,
        status: statusFilter || undefined,
        limit: 20,
        page: 1,
      };
      dispatch(getAllOrdersForAdminThunk(filters));
    },
    [dispatch, statusFilter]
  );

  // Handle search with debouncing
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchOrders(query);
    }, 300);
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    const status = e.target.value;
    setStatusFilter(status);
    setTimeout(() => fetchOrders(searchQuery), 100);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Handle order selection
  const handleSelectOrder = (order) => {
    if (localSelectedOrder?._id === order._id) {
      // Toggle expand/collapse
      setExpandedOrderId(expandedOrderId === order._id ? null : order._id);
    } else {
      setLocalSelectedOrder(order);
      setExpandedOrderId(order._id);
      setSelectedItemIndex(null);
    }
  };

  // Handle item selection within order
  const handleSelectItem = (index) => {
    setSelectedItemIndex(index);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (!localSelectedOrder) {
      notifyError("Please select an order");
      return;
    }

    const selectedItem =
      selectedItemIndex !== null
        ? localSelectedOrder.items?.[selectedItemIndex]
        : null;

    const result = {
      orderId: localSelectedOrder.orderId,
      orderMongoId: localSelectedOrder._id,
      order: localSelectedOrder,
      itemIndex: selectedItemIndex,
      item: selectedItem,
      // Customer info extracted from order
      customer: {
        userId: localSelectedOrder.userId?._id || null,
        name: getCustomerName(localSelectedOrder),
        phone: getCustomerPhone(localSelectedOrder),
        isGuest: localSelectedOrder.guestCustomer?.isAnonymous || false,
      },
      // Item details for auto-fill
      itemDetails: selectedItem ? getItemDetails(selectedItem) : null,
    };

    onOrderSelected(result);
    notifySuccess(
      `Order ${localSelectedOrder.orderId} selected${
        selectedItemIndex !== null ? ` (Item #${selectedItemIndex + 1})` : ""
      }`
    );
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    setLocalSelectedOrder(null);
    setSelectedItemIndex(null);
    setExpandedOrderId(null);
    onClose();
  };

  // Get status badge variant
  const getStatusBadge = (status) => {
    const statusConfig = ORDER_STATUSES.find((s) => s.value === status);
    return (
      <Badge variant={statusConfig?.color || "secondary"} size="sm">
        {statusConfig?.label || status}
      </Badge>
    );
  };

  // Check if item needs karigar work
  const itemNeedsKarigarWork = (item) => {
    if (!item) return false;
    const snapshot = item.productSnapshot || {};
    return (
      KARIGAR_WORK_TYPES.includes(item.workType) ||
      KARIGAR_WORK_TYPES.includes(snapshot.workType) ||
      item.requiresMaking ||
      item.isCustomOrder ||
      snapshot.requiresMaking ||
      // Items with subType that typically need work
      [
        "RING",
        "NECKLACE",
        "BRACELET",
        "BANGLE",
        "CHAIN",
        "PENDANT",
        "EARRING",
      ].includes(snapshot.subType)
    );
  };

  // Get items that need karigar work
  const getKarigarItems = (order) => {
    if (!order?.items) return [];
    return order.items
      .map((item, index) => ({ ...item, originalIndex: index }))
      .filter((item) => itemNeedsKarigarWork(item));
  };

  // Filter orders based on search and karigar work filter
  const filteredOrders = orders?.filter((order) => {
    if (filterByKarigarWork) {
      const karigarItems = getKarigarItems(order);
      if (karigarItems.length === 0) return false;
    }
    return true;
  });

  // Helper to get customer name from order
  const getCustomerName = (order) => {
    if (order.userId?.name) return order.userId.name;
    if (order.guestCustomer?.name) return order.guestCustomer.name;
    return "N/A";
  };

  // Helper to get customer phone from order
  const getCustomerPhone = (order) => {
    if (order.userId?.phoneNumber) return order.userId.phoneNumber;
    return null;
  };

  // Helper to get order total
  const getOrderTotal = (order) => {
    return (
      order.orderSummary?.finalTotals?.grandTotal ||
      order.orderSummary?.finalTotals?.netPayable ||
      0
    );
  };

  // Helper to get item name from productSnapshot
  const getItemName = (item) => {
    return (
      item.productSnapshot?.name ||
      item.name ||
      item.productName ||
      "Unknown Item"
    );
  };

  // Helper to get item details from productSnapshot
  const getItemDetails = (item) => {
    const snapshot = item.productSnapshot || {};
    return {
      name: snapshot.name || item.name,
      sku: snapshot.sku || item.sku,
      type: snapshot.type,
      subType: snapshot.subType,
      metalType: snapshot.type || snapshot.metalDetails?.color,
      purity: snapshot.metalDetails?.purity,
      weight: snapshot.netWeight || snapshot.grossWeight,
      workType: item.workType,
    };
  };

  // Render order card
  const renderOrderCard = (order) => {
    const isSelected =
      localSelectedOrder?._id === order._id ||
      localSelectedOrder?.orderId === order.orderId;
    const isExpanded =
      expandedOrderId === order._id || expandedOrderId === order.orderId;
    const karigarItems = getKarigarItems(order);
    const hasKarigarItems = karigarItems.length > 0;
    const customerName = getCustomerName(order);
    const customerPhone = getCustomerPhone(order);
    const orderTotal = getOrderTotal(order);

    return (
      <div
        key={order._id}
        onClick={() => handleSelectOrder(order)}
        className={`p-4 border-2 m-2 rounded-lg cursor-pointer transition-all ${
          isSelected
            ? "border-primary"
            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        {/* Order Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary">
                {order.orderId}
              </span>
              {getStatusBadge(order.status)}
              {order.orderType && (
                <Badge variant="secondary" size="sm">
                  {order.orderType.replace(/_/g, " ")}
                </Badge>
              )}
              {hasKarigarItems && (
                <Badge variant="info" size="sm">
                  <SparklesIcon className="w-3 h-3 mr-1 inline" />
                  Karigar Work
                </Badge>
              )}
              {isSelected && (
                <CheckCircleIcon className="w-5 h-5 text-primary" />
              )}
            </div>

            {/* Customer Info */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <UserIcon className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{customerName}</span>
                {order.guestCustomer?.isAnonymous && (
                  <Badge variant="warning" size="sm">
                    Guest
                  </Badge>
                )}
              </div>
              {customerPhone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <PhoneIcon className="w-4 h-4 text-gray-400" />
                  <span>{customerPhone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="text-right">
            <div className="flex items-center gap-1 text-lg font-semibold text-gray-900">
              <CurrencyRupeeIcon className="w-5 h-5" />
              {Math.abs(orderTotal).toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-gray-500">
              {order.items?.length || 0} item(s)
            </div>
            {order.orderSummary?.finalTotals?.totalExchangeValue > 0 && (
              <div className="text-xs text-orange-600 mt-1">
                Exchange: ₹
                {order.orderSummary.finalTotals.totalExchangeValue.toLocaleString(
                  "en-IN"
                )}
              </div>
            )}
          </div>
        </div>

        {/* Order Meta */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <CalendarIcon className="w-3 h-3" />
            {order.createdAt
              ? format(new Date(order.createdAt), "dd MMM yyyy")
              : "N/A"}
          </div>
          {order.fulfillment?.location?.branchCode && (
            <div className="flex items-center gap-1">
              <ShoppingBagIcon className="w-3 h-3" />
              {order.fulfillment.location.branchCode}
            </div>
          )}
          {order.payment?.status && (
            <Badge
              variant={
                order.payment.status === "COMPLETED"
                  ? "success"
                  : order.payment.status === "PARTIAL"
                  ? "warning"
                  : "secondary"
              }
              size="sm"
            >
              {order.payment.status}
            </Badge>
          )}
          <ChevronDownIcon
            className={`w-4 h-4 ml-auto transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Expanded Items */}
        {isExpanded && showItemSelection && order.items?.length > 0 && (
          <div
            className="mt-4 pt-4 border-t border-gray-200 space-y-2"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm font-medium text-gray-700 mb-3">
              Select Item for Karigar Job:
            </p>
            {order.items.map((item, index) => {
              const needsWork = itemNeedsKarigarWork(item);
              const isItemSelected = selectedItemIndex === index;
              const itemDetails = getItemDetails(item);

              return (
                <div
                  key={item._id || index}
                  className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    isItemSelected
                      ? "border-primary"
                      : needsWork
                      ? "border-purple-200 bg-purple-50 hover:border-purple-300"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelectItem(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded flex items-center justify-center text-xs font-semibold ${
                          isItemSelected
                            ? "bg-primary text-white"
                            : needsWork
                            ? "bg-purple-200 text-purple-800"
                            : "bg-gray-200 text-gray-700"
                        }`}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900">
                          {itemDetails.name || `Item ${index + 1}`}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1 flex-wrap">
                          {itemDetails.sku && (
                            <span className="flex items-center gap-1">
                              <CubeIcon className="w-3 h-3" />
                              {itemDetails.sku}
                            </span>
                          )}
                          {itemDetails.type && (
                            <Badge variant="secondary" size="sm">
                              {itemDetails.type}
                            </Badge>
                          )}
                          {itemDetails.purity && (
                            <Badge variant="info" size="sm">
                              {itemDetails.purity}
                            </Badge>
                          )}
                          {itemDetails.weight && (
                            <span className="flex items-center gap-1">
                              <ScaleIcon className="w-3 h-3" />
                              {itemDetails.weight}g
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {itemDetails.subType && (
                        <Badge variant="secondary" size="sm">
                          {itemDetails.subType}
                        </Badge>
                      )}
                      {needsWork && (
                        <Badge variant="info" size="sm">
                          <ClipboardListIcon className="w-3 h-3 mr-1 inline" />
                          {itemDetails.workType || "Making"}
                        </Badge>
                      )}
                      {isItemSelected && (
                        <CheckCircleIcon className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render section
  const renderSection = (sectionName, title, icon, children) => {
    const isExpanded = expandedSections[sectionName];

    return (
      <div className="border border-gray-200 rounded-lg mb-4">
        <button
          type="button"
          onClick={() => toggleSection(sectionName)}
          className={`w-full px-4 py-3 flex items-center justify-between ${
            isExpanded ? "bg-gray-50" : "bg-white"
          } hover:bg-gray-50 transition-colors rounded-t-lg`}
        >
          <div className="flex items-center gap-2">
            {React.createElement(icon, { className: "w-5 h-5 text-gray-600" })}
            <span className="font-medium text-gray-900">{title}</span>
          </div>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {isExpanded && (
          <div className="p-4 border-t border-gray-200">{children}</div>
        )}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleClose}
      title={
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5" />
          {title}
          {localSelectedOrder && (
            <Badge variant="success" size="sm">
              Selected
            </Badge>
          )}
        </div>
      }
      maxWidth="3xl"
    >
      {/* Search & Filters */}
      <div className="space-y-4 mb-4">
        {/* Search Input */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search by order number, customer name, or phone..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                fetchOrders("");
              }}
              className="absolute right-10 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => fetchOrders(searchQuery)}
            disabled={loadingList}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <RefreshIcon
              className={`w-5 h-5 ${loadingList ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        {/* Filters Section */}
        {renderSection(
          "filters",
          "Filters",
          ClipboardListIcon,
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Status
              </label>
              <select
                value={statusFilter}
                onChange={handleStatusChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {ORDER_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <p className="text-sm text-gray-500">
                Tip: Select an order and optionally choose a specific item for
                the karigar job
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Orders List */}
      <div className="max-h-96 overflow-y-auto">
        {loadingList ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-2 text-gray-600">
              <svg
                className="animate-spin h-5 w-5 text-primary"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span>Loading orders...</span>
            </div>
          </div>
        ) : filteredOrders && filteredOrders.length > 0 ? (
          <div className="space-y-3">
            {filteredOrders.map((order) => renderOrderCard(order))}
          </div>
        ) : (
          <div className="text-center py-12">
            <ShoppingBagIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-2">No orders found</p>
            <p className="text-sm text-gray-400">
              Try adjusting your search or filters
            </p>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      {pagination && pagination.total > 0 && (
        <div className="text-xs text-gray-500 text-center py-2 border-t border-gray-100 mt-4">
          Showing {filteredOrders?.length || 0} of {pagination.total} orders
        </div>
      )}

      {/* Selected Order Summary */}
      {localSelectedOrder && (
        <div className="mt-4 p-4 border border-primary rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingBagIcon className="w-8 h-8 text-primary" />
              <div>
                <p className="font-semibold text-primary">
                  {localSelectedOrder.orderId}
                </p>
                <p className="text-sm text-gray-600">
                  {getCustomerName(localSelectedOrder)}
                  {localSelectedOrder.guestCustomer?.isAnonymous && (
                    <span className="ml-1 text-orange-600">(Guest)</span>
                  )}
                  {selectedItemIndex !== null && (
                    <span className="ml-2 text-primary font-medium">
                      • Item #{selectedItemIndex + 1}:{" "}
                      {getItemDetails(
                        localSelectedOrder.items?.[selectedItemIndex]
                      )?.name || "Selected"}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setLocalSelectedOrder(null);
                setSelectedItemIndex(null);
                setExpandedOrderId(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!localSelectedOrder}
        >
          <CheckCircleIcon className="w-4 h-4 mr-2" />
          Confirm Selection
        </Button>
      </div>
    </Modal>
  );
};

export default OrderSelection;
